// src/oura/components/docs/write/DocMessageView.tsx
//
// The review email and the chaser (B18), a view inside the doc drawer. It is
// onboarding's compose drawer with 2 more kinds, doc_review and doc_chaser:
// the same fields, the same sender line and the same send route
// (`POST /businesses/{id}/onboarding/messages`), from Angel at oura@ with a
// copy in the oura@ inbox. One drawer at a time, so it opens as a view of
// the doc's sheet rather than a second sheet over it.
//
// Oura drafts it from the doc: its link, the number of questions waiting on
// the client and the reply-by date. The named person signed in reads it,
// changes anything, and sends it; their name is the sender on record. The
// chaser is offered only once the reply-by has passed.

import { useEffect, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";
import { getDocMessageDraft, sendDocMessage, type DocMessageDraft } from "@service/aeo/clientDocsWrite";
import type { DocsFailure, StaffDocument } from "@service/aeo/clientDocs";
import { cn } from "../../../lib/cn";
import { DrawerBlock, DrawerFold, DrawerFoot, DrawerPulse, InnerStat, PillButton, SURFACE, TYPE, type DrawerFootMessage } from "@oura/ui";
import { docDate, docsFailureText, plural } from "@oura/docs";
import { formatPeople } from "../../../onboarding/drafts";
import { clearFixed, readMessage, sameMessage, toMessageText, type MessageErrors, type MessageText } from "../../../onboarding/messageText";
import { MessageFields, SenderLine } from "../../onboarding/fields";
import { MESSAGE_KIND, chaserDue, lastMessageOf, useDocMessages, type DocWriteView } from "./model";
import { useSignedInName } from "@oura/docs/signedIn";

/** What staff typed, kept while the view is closed, by doc and kind. Sending clears it. */
const edits = new Map<string, MessageText>();

type DraftLoad = { status: "loading" } | { status: "ready"; draft: DocMessageDraft } | { status: "error"; failure: DocsFailure };

export function DocMessageView({
    view,
    doc,
    businessId,
    now,
    onBusy,
    onView,
}: {
    view: "review" | "chaser";
    doc: StaffDocument;
    businessId: string;
    now: number;
    onBusy: (busy: boolean) => void;
    onView: (view: DocWriteView | "doc") => void;
}) {
    const kind = MESSAGE_KIND[view];
    const key = `${doc.id}:${kind}`;
    const name = useSignedInName();
    const messages = useDocMessages(businessId);
    const last = lastMessageOf(messages.data, doc.id, kind);
    const open = view === "review" || chaserDue(doc, now);

    const [load, setLoad] = useState<DraftLoad>({ status: "loading" });
    const [text, setText] = useState<MessageText | null>(() => edits.get(key) ?? null);
    const [errors, setErrors] = useState<MessageErrors | null>(null);
    const [message, setMessage] = useState<DrawerFootMessage | null>(null);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [copied, setCopied] = useState(false);
    const [tries, setTries] = useState(0);

    useEffect(() => {
        if (!open) return;
        let alive = true;
        setLoad({ status: "loading" });
        void getDocMessageDraft(businessId, kind, [doc.id]).then((r) => {
            if (!alive) return;
            setLoad(r.ok ? { status: "ready", draft: r.data } : { status: "error", failure: r });
        });
        return () => {
            alive = false;
        };
    }, [businessId, kind, doc.id, open, tries]);

    const back = (primary = false) => (
        <PillButton variant={primary ? "primary" : "secondary"} disabled={sending} onClick={() => onView("doc")}>
            Back to the doc
        </PillButton>
    );

    if (!open) {
        return (
            <>
                <DrawerBlock>
                    <p className="m-0 text-gray-700">
                        The chaser opens once the reply-by has passed{docDate(doc.replyBy) ? `: ${docDate(doc.replyBy)}` : ""}, with questions still waiting on the client.
                    </p>
                </DrawerBlock>
                <DrawerFoot second={back()} />
            </>
        );
    }

    if (load.status !== "ready") {
        const notShared = load.status === "error" && load.failure.code === "not_shared";
        return (
            <>
                <DrawerBlock>
                    {load.status === "loading" ? (
                        <DrawerPulse lines={4} label="Writing Oura's draft" />
                    ) : (
                        <p className="m-0 text-gray-700">{docsFailureText(load.failure, "the draft")}</p>
                    )}
                </DrawerBlock>
                <DrawerFoot
                    first={
                        notShared ? (
                            <PillButton variant="primary" onClick={() => onView("share")}>
                                Share with the client
                            </PillButton>
                        ) : load.status === "error" ? (
                            <PillButton onClick={() => setTries((n) => n + 1)}>Try again</PillButton>
                        ) : undefined
                    }
                    second={back()}
                />
            </>
        );
    }

    const d = load.draft;
    const oura = toMessageText(d);
    const value = text ?? oura;

    const change = (next: MessageText) => {
        setErrors((was) => clearFixed(was, value, next));
        setMessage(null);
        setText(next);
        edits.set(key, next);
    };

    const send = async () => {
        const read = readMessage(value);
        setErrors(read.errors);
        setMessage(null);
        if (!read.draft) return;
        setSending(true);
        onBusy(true);
        const r = await sendDocMessage(businessId, {
            kind,
            to: read.draft.to,
            cc: read.draft.cc,
            subject: read.draft.subject,
            body: read.draft.body,
            documentIds: d.documentIds,
            questionCount: d.questionCount,
            replyBy: d.replyBy,
        });
        setSending(false);
        onBusy(false);
        if (!r.ok) {
            setMessage({ tone: "error", text: docsFailureText(r, "the email") });
            return;
        }
        edits.delete(key);
        setText(null);
        setSent(true);
        setMessage({ tone: "success", text: `Sent to ${formatPeople(r.data.to)}.` });
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(`${value.subject}\n\n${value.body}`);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1600);
        } catch {
            setMessage({ tone: "error", text: "Could not copy. Select the text and copy it by hand." });
        }
    };

    const edited = !sameMessage(value, oura);
    const first = sent ? (
        back(true)
    ) : (
        <PillButton variant="primary" busy={sending} onClick={() => void send()}>
            Send from Oura
        </PillButton>
    );
    const second = sent ? undefined : edited ? (
        <PillButton
            disabled={sending}
            onClick={() => {
                setText(null);
                setErrors(null);
                edits.delete(key);
            }}
        >
            Back to Oura&rsquo;s draft
        </PillButton>
    ) : (
        <PillButton icon={copied ? CheckIcon : CopyIcon} disabled={sending} onClick={() => void copy()}>
            {copied ? "Copied" : "Copy"}
        </PillButton>
    );

    return (
        <>
            <DrawerBlock label="In this email">
                <div className="mt-2 grid grid-cols-2 gap-2">
                    <InnerStat label="Waiting on client" value={<span className="tabular-nums">{plural(d.questionCount, "question")}</span>} />
                    <InnerStat label="Reply by" value={docDate(d.replyBy) ?? "Not set"} />
                </div>
                <p className={cn(TYPE.tc, "m-0 mt-2 break-words text-gray-500")}>The doc&rsquo;s link is in the message.</p>
            </DrawerBlock>

            {last && (
                <DrawerFold id={`staff-doc.${view}-last`} label={view === "review" ? "Last review email" : "Last chaser"} note={`${docDate(last.sentAt) ?? ""} by ${last.sentBy}`.trim()}>
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                        To {formatPeople(last.to)} · {plural(last.questionCount, "question")}
                    </p>
                    <p className={cn(TYPE.tb, "m-0 mt-1 font-medium text-gray-900")}>{last.subject}</p>
                    <p className={cn(TYPE.tc, "m-0 mt-1 whitespace-pre-wrap break-words text-gray-700")}>{last.body}</p>
                </DrawerFold>
            )}

            {!sent && (
                <>
                    <div className={SURFACE.block}>
                        <MessageFields value={value} onChange={change} errors={errors} disabled={sending} rows={8} />
                    </div>
                    <SenderLine />
                    <p className={cn(TYPE.tc, "m-0 -mt-1 text-gray-500")}>{name ? `${name} sends it and is the sender on record.` : "You send it and are the sender on record."}</p>
                </>
            )}

            <DrawerFoot message={message} first={first} second={second} />
        </>
    );
}
